import React, { useState, useEffect, useCallback, useMemo } from 'react';
import rbacService from '../../services/rbacService';
import inviteService from '../../services/inviteService';
import { useAuth } from '../../contexts/AuthContext';
import VisualSelector from '../../components/common/VisualSelector';
import useDebounce from '../../hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import VerificationModal from './VerificationModal';
import './AdminUsersPage.css';

const ComputedPermissionsList = ({ profileId }) => {
    const { t } = useTranslation();
    const [perms, setPerms] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        rbacService.getProfilePermissions(profileId)
            .then(setPerms)
            .catch(err => console.error('Failed to load computed permissions', err))
            .finally(() => setLoading(false));
    }, [profileId]);

    return (
        <div className="computed-permissions-section">
            <h5>{t('computed_permissions')}</h5>
            {loading ? <span className="loading-text">{t('loading_permissions')}</span> : (
                <div className="perms-cloud">
                    {perms.map(p => <span key={p} className="badge-perm">{p}</span>)}
                    {perms.length === 0 && <span className="empty-msg">{t('no_permissions_resolved')}</span>}
                </div>
            )}
        </div>
    );
};

const AdminUsersPage = () => {
    const { t } = useTranslation();
    const { token } = useAuth();
    const [usersData, setUsersData] = useState({ users: [], total: 0, pages: 1, currentPage: 1 });
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);
    const [sortConfig, setSortConfig] = useState({ by: 'username', order: 'ASC' });
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const [groups, setGroups] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editingProfile, setEditingProfile] = useState(null);
    const [filterNoProfile, setFilterNoProfile] = useState(false);

    // Form for new profile
    const [showProfileForm, setShowProfileForm] = useState(false);
    const [newProfile, setNewProfile] = useState({ artistName: '', roleId: '' });

    // Grant Invites Form
    const [showGrantForm, setShowGrantForm] = useState(false);
    const [grantAmount, setGrantAmount] = useState(5);

    // Confirm Modal State
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => { } });

    // Verification Modal State
    const [verificationModal, setVerificationModal] = useState({ isOpen: false, profile: null });

    const fetchUsers = useCallback(async (page = 1, search = '') => {
        setLoading(true);
        try {
            const { by, order } = sortConfig;
            const data = await rbacService.getUsersPaginated(page, 50, search, by, order);
            setUsersData(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [sortConfig]);

    useEffect(() => {
        fetchUsers(1, debouncedSearch);
    }, [debouncedSearch, sortConfig, fetchUsers]);

    // Separate effect to re-sync selectedUser when usersData changes
    // This avoids the infinite loop because it doesn't trigger fetchUsers again
    useEffect(() => {
        if (selectedUser) {
            const updated = usersData.users.find(u => u.id === selectedUser.id);
            if (updated && JSON.stringify(updated) !== JSON.stringify(selectedUser)) {
                setSelectedUser(updated);
            }
        }
    }, [usersData, selectedUser]);

    const handleSort = (field) => {
        setSortConfig(prev => ({
            by: field,
            order: (prev.by === field && prev.order === 'ASC') ? 'DESC' : 'ASC'
        }));
    };

    const renderSortArrow = (field) => {
        if (sortConfig.by !== field) return <span className="sort-placeholder">↕</span>;
        return sortConfig.order === 'ASC' ? ' ↑' : ' ↓';
    };

    useEffect(() => {
        Promise.all([
            rbacService.getRoles(1, 9999, '', '', ''), // Fetch all roles
            rbacService.getGroups(1, 9999, '', '', ''), // Fetch all groups
            rbacService.getPermissions(1, 9999, '', '', '') // Fetch all permissions
        ]).then(([r, g, p]) => {
            setRoles(r.items || r);
            setGroups(g.items || g);
            setAllPermissions(p.items || p);
        }).catch(console.error);
    }, []);

    const filteredUsers = useMemo(() => {
        let users = usersData.users || [];
        if (filterNoProfile) {
            users = users.filter(u => !u.profiles || u.profiles.length === 0);
        }
        return users;
    }, [usersData.users, filterNoProfile]);

    const handleCreateProfile = async (e) => {
        e.preventDefault();
        try {
            await rbacService.createProfile(selectedUser.id, newProfile.roleId, newProfile.artistName);
            setShowProfileForm(false);
            setNewProfile({ artistName: '', roleId: '' });
            fetchUsers(usersData.currentPage, searchTerm);
            toast.success('Profile created successfully');
        } catch (e) {
            toast.error(e.response?.data?.message || 'Failed to create profile');
        }
    };

    const handleDeleteProfile = (profileId) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Profile',
            message: 'Are you sure you want to delete this profile? This action cannot be undone.',
            onConfirm: async () => {
                try {
                    await rbacService.deleteProfile(profileId);
                    fetchUsers(usersData.currentPage, searchTerm);
                    toast.success('Profile deleted');
                } catch (e) {
                    toast.error('Failed to delete profile');
                }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleAssignGroups = async (profileId, groupIds) => {
        try {
            await rbacService.assignGroupsToProfile(profileId, groupIds);
            fetchUsers(usersData.currentPage, searchTerm);
            toast.success('Groups updated');
        } catch (e) {
            toast.error('Failed to update groups');
        }
    };

    const handleSetRole = async (profileId, roleId) => {
        try {
            await rbacService.setRoleForProfile(profileId, roleId);
            fetchUsers(usersData.currentPage, searchTerm);
            // Update local state if editing
            if (editingProfile && editingProfile.id === profileId) {
                const newRole = roles.find(r => r.id === roleId);
                setEditingProfile(prev => ({ ...prev, role: newRole }));
            }
            toast.success('Role updated');
        } catch (e) {
            toast.error('Failed to set role');
        }
    };

    const handleAssignPermissions = async (profileId, permissionIds) => {
        try {
            await rbacService.assignPermissionsToProfile(profileId, permissionIds);
            fetchUsers(usersData.currentPage, searchTerm);

            if (editingProfile && editingProfile.id === profileId) {
                const newPerms = allPermissions.filter(p => permissionIds.includes(p.id));
                setEditingProfile(prev => ({ ...prev, direct_permissions: newPerms }));
            }
            toast.success('Permissions updated');
        } catch (e) {
            toast.error('Failed to update permissions');
        }
    };

    const handleSelectUser = (user) => {
        setSelectedUser(user);
        setEditingProfile(null);
        setSelectedUser(user);
        setEditingProfile(null);
        setShowProfileForm(false);
        setShowGrantForm(false);
    };

    const handleEditUser = () => {
        toast.info('Edit User functionality (e.g., username/email) could be added here in a modal.');
    };

    const handleDeleteUser = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete User',
            message: `Are you sure you want to delete user ${selectedUser.username}? This will delete all their profiles too.`,
            onConfirm: async () => {
                try {
                    await rbacService.deleteUser(selectedUser.id);
                    setSelectedUser(null);
                    fetchUsers(usersData.currentPage, searchTerm);
                    toast.success('User deleted successfully');
                } catch (e) {
                    toast.error('Failed to delete user');
                }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleGrantInvites = async (e) => {
        e.preventDefault();
        try {
            await inviteService.grantInvites(token, selectedUser.id, parseInt(grantAmount));
            toast.success(t('invites_granted_success', `Granted ${grantAmount} invites!`));
            setShowGrantForm(false);
        } catch (error) {
            toast.error(t('invites_granted_error', 'Failed to grant invites'));
        }
    };

    const handleVerificationSubmit = async (expiry) => {
        const { profile } = verificationModal;
        try {
            if (profile.isVerified) {
                await rbacService.unverifyProfile(profile.id);
            } else {
                await rbacService.verifyProfile(profile.id, expiry);
            }
            fetchUsers(usersData.currentPage, searchTerm);
            toast.success(t('verification_updated', 'Verificação atualizada'));
        } catch (err) {
            toast.error(t('verification_error', 'Erro ao atualizar verificação'));
        } finally {
            setVerificationModal({ isOpen: false, profile: null });
        }
    };

    return (
        <div className="admin-users-revamp">
            <div className="users-list-section">
                <div className="section-header">
                    <h2>{t('admin_users')}</h2>
                    <div className="list-controls">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder={t('search_username')}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <label className="filter-toggle">
                            <input
                                type="checkbox"
                                checked={filterNoProfile}
                                onChange={e => setFilterNoProfile(e.target.checked)}
                            />
                            {t('no_profile_only')}
                        </label>
                    </div>
                </div>

                <div className="users-table-container">
                    {loading && usersData.users.length === 0 && <div className="loading-overlay">Loading...</div>}
                    <table className="modern-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('username')} className="sortable">
                                    {t('username')} {renderSortArrow('username')}
                                </th>
                                <th>{t('user_profiles')}</th>
                                <th onClick={() => handleSort('createdAt')} className="sortable">
                                    {t('joined_at')} {renderSortArrow('createdAt')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map(user => {
                                const hasNoProfile = !user.profiles || user.profiles.length === 0;
                                return (
                                    <tr
                                        key={user.id}
                                        className={`${selectedUser?.id === user.id ? 'active' : ''} ${hasNoProfile ? 'needs-profile' : ''}`}
                                        onClick={() => handleSelectUser(user)}
                                    >
                                        <td>
                                            <div className="user-info-cell">
                                                <strong>{user.username}</strong>
                                                <span className="email-sub">{user.email}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="profile-tags">
                                                {user.profiles?.map(p => (
                                                    <span key={p.id} className="profile-tag">
                                                        {p.role?.name || t('artist')}
                                                    </span>
                                                ))}
                                                {hasNoProfile && <span className="profile-tag badge-warning">{t('no_profile_only')}</span>}
                                            </div>
                                        </td>
                                        <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="pagination">
                    <button
                        disabled={usersData.currentPage === 1}
                        onClick={() => fetchUsers(usersData.currentPage - 1, searchTerm)}
                    >{t('previous_page')}</button>
                    <span>{t('page_of', { current: usersData.currentPage, total: usersData.pages })}</span>
                    <button
                        disabled={usersData.currentPage === usersData.pages}
                        onClick={() => fetchUsers(usersData.currentPage + 1, searchTerm)}
                    >{t('next_page')}</button>
                </div>
            </div>

            <div className="user-details-section">
                {selectedUser ? (
                    <div className="details-container">
                        <div className="user-header">
                            <div>
                                <h3>{selectedUser.username}</h3>
                                <p className="user-id">ID: {selectedUser.id}</p>
                            </div>
                            <div className="user-actions">
                                <button className="btn-secondary" onClick={handleEditUser}>{t('edit_profile')}</button>
                                <button className="btn-secondary" onClick={() => setShowGrantForm(true)}>🎟️ {t('grant_invites', 'Liberar Convites')}</button>
                                <button className="btn-danger" onClick={handleDeleteUser}>{t('delete_user') || 'Delete User'}</button>
                                <button className="btn-primary" onClick={() => setShowProfileForm(true)}>+ {t('add_role_profile')}</button>
                            </div>
                        </div>

                        {showGrantForm && (
                            <div className="admin-card profile-form-card" style={{ borderColor: '#9b59b6' }}>
                                <h4>🎟️ {t('grant_invites', 'Liberar Convites')}</h4>
                                <form onSubmit={handleGrantInvites}>
                                    <div className="form-group">
                                        <label>{t('amount', 'Quantidade')}</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={grantAmount}
                                            onChange={e => setGrantAmount(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="form-actions">
                                        <button type="submit" className="btn-primary" style={{ backgroundColor: '#9b59b6' }}>{t('confirm_grant', 'Liberar')}</button>
                                        <button type="button" className="btn-text" onClick={() => setShowGrantForm(false)}>{t('cancel')}</button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {showProfileForm && (
                            <div className="admin-card profile-form-card">
                                <h4>{t('add_role_profile')}</h4>
                                <form onSubmit={handleCreateProfile}>
                                    <div className="form-group">
                                        <label>{t('artist_name')}</label>
                                        <input
                                            type="text"
                                            value={newProfile.artistName}
                                            onChange={e => setNewProfile({ ...newProfile, artistName: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('artwork_category')} ({t('artist')})</label>
                                        <select
                                            value={newProfile.roleId}
                                            onChange={e => setNewProfile({ ...newProfile, roleId: e.target.value })}
                                            required
                                        >
                                            <option value="">{t('select_category')}...</option>
                                            {roles.map(r => {
                                                const isOwned = selectedUser.profiles?.some(p => p.roleId === r.id);
                                                return (
                                                    <option key={r.id} value={r.id} disabled={isOwned}>
                                                        {r.name} {isOwned ? `(${t('already_owned')})` : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className="form-actions">
                                        <button type="submit" className="btn-primary">{t('add_role_profile')}</button>
                                        <button type="button" className="btn-text" onClick={() => setShowProfileForm(false)}>{t('cancel')}</button>
                                    </div>
                                </form>
                            </div>
                        )}

                        <div className="profiles-list">
                            {!editingProfile ? (
                                <>
                                    {selectedUser.profiles?.map(profile => (
                                        <div key={profile.id} className="admin-card user-profile-card">
                                            <div className="profile-card-content">
                                                <div className="profile-main-info">
                                                    <h4>{profile.artistName || 'Default Profile'}</h4>
                                                    <div className="profile-meta">
                                                        <span className="badge-role">{profile.role?.name || 'No Role'}</span>
                                                        <span className="profile-id-mini">ID: {profile.id.substring(0, 8)}</span>
                                                    </div>
                                                </div>

                                                <div className="profile-groups-info">
                                                    <div className="info-label">{t('assigned_groups')}:</div>
                                                    <div className="info-tags">
                                                        {profile.groups?.map(g => (
                                                            <span key={g.id} className="tag-info">{g.name}</span>
                                                        ))}
                                                        {(!profile.groups || profile.groups.length === 0) && <span className="empty-msg">{t('all')}</span>}
                                                    </div>
                                                </div>

                                                <div className="profile-verification-info">
                                                    <label className="verification-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={profile.isVerified}
                                                            onChange={() => setVerificationModal({ isOpen: true, profile })}
                                                        />
                                                        <span>{profile.isVerified ? `✅ ${t('verified', 'Verificado')}` : t('not_verified', 'Não Verificado')}</span>
                                                        {profile.isVerified && profile.verifiedUntil && (
                                                            <span className="expiry-hint"> (Até: {new Date(profile.verifiedUntil).toLocaleDateString()})</span>
                                                        )}
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="profile-card-actions">
                                                <button className="btn-outline" onClick={() => setEditingProfile(profile)}>{t('edit_permissions')}</button>
                                                <button className="btn-danger-icon" onClick={() => handleDeleteProfile(profile.id)}>✖</button>
                                            </div>
                                        </div>
                                    ))}
                                    {(!selectedUser.profiles || selectedUser.profiles.length === 0) && (
                                        <div className="empty-state-card">
                                            <p>{t('no_results_found')}</p>
                                            <button className="btn-primary" onClick={() => setShowProfileForm(true)}>{t('add_role_profile')}</button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="profile-editor-view">
                                    <div className="editor-nav">
                                        <button className="btn-text" onClick={() => setEditingProfile(null)}>← {t('back_to_profiles')}</button>
                                        <h4>{t('edit_profile')}: {editingProfile.artistName}</h4>
                                    </div>

                                    <div key={editingProfile.id} className="admin-card user-profile-edit">
                                        <div className="profile-edit-header">
                                            <div className="profile-info">
                                                <div className="role-selector-box">
                                                    <label>Role / Profile Type:</label>
                                                    <select
                                                        value={editingProfile.role?.id || ''}
                                                        onChange={(e) => handleSetRole(editingProfile.id, e.target.value)}
                                                    >
                                                        {roles.map(r => {
                                                            const isOwned = selectedUser.profiles?.some(p => p.roleId === r.id && p.id !== editingProfile.id);
                                                            return (
                                                                <option key={r.id} value={r.id} disabled={isOwned}>
                                                                    {r.name} {isOwned ? `(${t('used_in_another_profile')})` : ''}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="inherited-section">
                                            <div className="inherited-header">
                                                <strong>{t('inherited_via_role')} ({editingProfile.role?.name}):</strong>
                                            </div>
                                            <div className="inherited-content">
                                                <div className="inherited-block">
                                                    <span className="block-label">{t('default_groups')}:</span>
                                                    <div className="inherited-tags">
                                                        {groups.filter(g => editingProfile.role?.groups?.some(rg => rg.id === g.id && rg.RoleGroup?.is_default)).map(ig => (
                                                            <span key={ig.id} className="badge-inherited group">{ig.name}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="assignment-grids two-col">
                                            <div className="assignment-item">
                                                <VisualSelector
                                                    title={t('optional_groups_for', { role: editingProfile.role?.name || t('artist') })}
                                                    subtitle={t('select_up_to_3')}
                                                    items={groups.filter(g => {
                                                        const roleAssoc = editingProfile.role?.groups?.find(rg => rg.id === g.id);
                                                        return roleAssoc && !roleAssoc.RoleGroup?.is_default;
                                                    })}
                                                    selectedIds={editingProfile.groups?.map(g => g.id) || []}
                                                    onToggle={(id) => {
                                                        const currentIds = editingProfile.groups?.map(g => g.id) || [];
                                                        const next = currentIds.includes(id)
                                                            ? currentIds.filter(cid => cid !== id)
                                                            : [...currentIds, id];
                                                        handleAssignGroups(editingProfile.id, next);
                                                        // Update local state for immediate feedback
                                                        setEditingProfile(prev => ({
                                                            ...prev,
                                                            groups: next.map(nid => groups.find(g => g.id === nid))
                                                        }));
                                                    }}
                                                />
                                            </div>
                                            <div className="assignment-item">
                                                <VisualSelector
                                                    title={t('direct_permission_overrides')}
                                                    subtitle={t('edit_permissions')}
                                                    items={allPermissions}
                                                    selectedIds={editingProfile.direct_permissions?.map(p => p.id) || []}
                                                    onToggle={(id) => {
                                                        const currentIds = editingProfile.direct_permissions?.map(p => p.id) || [];
                                                        const next = currentIds.includes(id)
                                                            ? currentIds.filter(cid => cid !== id)
                                                            : [...currentIds, id];
                                                        handleAssignPermissions(editingProfile.id, next);
                                                        // Update local state
                                                        setEditingProfile(prev => ({
                                                            ...prev,
                                                            direct_permissions: next.map(nid => allPermissions.find(p => p.id === nid))
                                                        }));
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <ComputedPermissionsList profileId={editingProfile.id} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="empty-details-view">
                        <div className="placeholder-icon">👤</div>
                        <p>Select a user to manage profiles and permissions</p>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />

            <VerificationModal
                isOpen={verificationModal.isOpen}
                onClose={() => setVerificationModal({ isOpen: false, profile: null })}
                onConfirm={handleVerificationSubmit}
                currentStatus={verificationModal.profile?.isVerified}
                currentExpiry={verificationModal.profile?.verifiedUntil}
            />
        </div>
    );
};

export default AdminUsersPage;
